import {useEffect, useMemo, useState} from 'react';

import SkeletonLine from '@common/components/Skeleton/SkeletonLine';
import Breadcrumb from '@common/components/Breadcrumb';
import useCompositionSheetStore from '@modules/piu/features/compositionSheet/store/compositionSheetStore';
import {
  selectCompositionSheetGmc,
  selectCompositionSheetIsLoading,
  selectCompositionSheetName,
  selectCompositionSheetProduct,
  selectCompositionSheetRegion,
  selectCompositionSheetRmc
} from '@modules/piu/features/compositionSheet/store/compositionSheetSelectors';

type CrumbId = 'product' | 'gmc' | 'rmc' | 'composition';

interface Crumb {
  id: CrumbId;
  name: string;
  tooltip?: string;
  action?: () => void;
  link?: string;
}

const CompositionBreadcrumb = () => {
  const compositionName = useCompositionSheetStore(selectCompositionSheetName);
  const product = useCompositionSheetStore(selectCompositionSheetProduct);
  const gmc = useCompositionSheetStore(selectCompositionSheetGmc);
  const region = useCompositionSheetStore(selectCompositionSheetRegion);
  const rmc = useCompositionSheetStore(selectCompositionSheetRmc);

  const isLoading = useCompositionSheetStore(selectCompositionSheetIsLoading);

  const [breadcrumbs, setBreadcrumbs] = useState<Crumb[]>([]);

  const MAX_CRUMBS_TO_DISPLAY = 3;

  const buildPath = (id: CrumbId) => {
    const basePath = `/product-sheet/${product?.mainItem}`;
    switch (id) {
      case 'product':
        return basePath;
      case 'gmc':
        return `${basePath}?version=${gmc?.uuid}`;
      case 'rmc':
        return `${basePath}?version=${gmc?.uuid}&region=${region?.uuid}`;
      default:
        return '';
    }
  };

  const buildBreadcrumbs = useMemo<Crumb[]>(() => {
    const crumbs: Crumb[] = [];

    if (product) {
      const crumb: Crumb = {
        id: 'product',
        name: product.name!,
        tooltip: `Return to Product`,
        link: buildPath('product')
      };
      crumbs.push(crumb);
    }

    if (gmc) {
      const crumb: Crumb = {
        id: 'gmc',
        name: gmc.name,
        tooltip: `Return to Global Marketing Content`,
        link: buildPath('gmc')
      };
      crumbs.push(crumb);
    }

    if (region && rmc) {
      const crumb: Crumb = {
        id: 'rmc',
        name: region.name,
        tooltip: `Return to Regional Marketing Content`,
        link: buildPath('rmc')
      };
      crumbs.push(crumb);
    }

    if (compositionName) {
      crumbs.push({
        id: 'composition',
        name: compositionName
      });
    }

    return crumbs;
  }, [product, gmc, rmc, region, compositionName]);

  useEffect(() => {
    setBreadcrumbs(buildBreadcrumbs);
  }, [buildBreadcrumbs]);

  if (!product) return null;

  return isLoading ? <SkeletonLine height={5} /> : <Breadcrumb crumbs={breadcrumbs} maxCrumbsToDisplay={MAX_CRUMBS_TO_DISPLAY} />;
};

export default CompositionBreadcrumb;
